import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

import gallery5 from "@/assets/gallery-5.jpg";

const OurStory = () => {
  const [storyImage, setStoryImage] = useState({ url: gallery5, alt: "Wedding ceremony first kiss" });

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const { data: locations, error: locError } = await supabase
          .from("portfolio_media_locations")
          .select("media_id")
          .eq("location", "our_story_page");

        if (locError) throw locError;

        if (locations && locations.length > 0) {
          const mediaIds = locations.map((l) => l.media_id);
          
          const { data: mediaData, error: mediaError } = await supabase
            .from("portfolio_media")
            .select("*")
            .in("id", mediaIds)
            .eq("is_active", true)
            .eq("media_type", "image")
            .order("sort_order", { ascending: true })
            .limit(1);

          if (mediaError) throw mediaError;

          if (mediaData && mediaData.length > 0) {
            setStoryImage({
              url: mediaData[0].url,
              alt: mediaData[0].alt_text || mediaData[0].title,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching our story page media:", error);
      }
    };

    fetchMedia();
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Story & Philosophy | Pixie Clicks | NJ, FL & CO</title>
        <meta
          name="description"
          content="Discover the Pixie Clicks story — our origin, philosophy & approach to capturing authentic wedding moments. Serving NJ, FL & CO."
        />
        <meta property="og:title" content="Our Story & Philosophy | Pixie Clicks" />
        <meta property="og:description" content="Discover our origin, philosophy, and approach to capturing authentic wedding moments with artistry and heart." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pixieclicks.com/our-story-philosophy" />
        <link rel="canonical" href="https://pixieclicks.com/our-story-philosophy" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "Our Story & Philosophy",
            "description": "The Pixie Clicks story — our origin, creative philosophy, and approach to capturing authentic wedding moments.",
            "mainEntity": {
              "@type": "LocalBusiness",
              "name": "Pixie Clicks",
              "areaServed": ["New Jersey", "Florida", "Colorado"],
              "foundingDate": "2019"
            }
          })}
        </script>
      </Helmet>
      <Layout>
        {/* Hero */}
        <section className="relative py-20 md:py-32">
          <div className="container-wide">
            <div className="mx-auto max-w-3xl text-center">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="font-serif text-4xl font-semibold leading-tight text-foreground md:text-5xl"
              >
                Our Story & <span className="text-primary">Philosophy</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mt-6 text-lg text-muted-foreground"
              >
                How a passion for authentic storytelling became a mission to make beautiful wedding coverage accessible to everyone.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Origin Story */}
        <section className="pb-16 md:pb-24">
          <div className="container-wide">
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <img
                  src={storyImage.url}
                  alt={storyImage.alt}
                  className="rounded-2xl shadow-elevated"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
                  How It All Began
                </h2>
                <p className="mt-6 text-muted-foreground">
                  Pixie Clicks started with a simple observation: too many couples were being priced out of quality wedding photography. We saw engaged couples stressed about budgets, settling for less, or going into debt for photos they deserved.
                </p>
                <p className="mt-4 text-muted-foreground">
                  We knew there had to be a better way. So we built a team of talented, passionate photographers and videographers who share our vision — that every love story deserves to be captured beautifully, regardless of the wedding budget.
                </p>
                <p className="mt-4 text-muted-foreground">
                  Today, we've captured over 200 weddings across New Jersey, Florida, Colorado, and beyond. Each one reminds us why we do this: the tears, the laughter, the first looks, and the last dances — these moments are priceless, and we're honored to preserve them.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Philosophy */}
        <section className="section-padding bg-muted/30">
          <div className="container-narrow">
            <div className="text-center">
              <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
                Our Creative Philosophy
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
                We approach every wedding with these guiding principles.
              </p>
            </div>

            <div className="mt-12 space-y-8">
              {[
                {
                  number: "01",
                  title: "Authentic Over Posed",
                  description: "We believe the most beautiful moments are the real ones. While we'll guide you for stunning portraits, we specialize in capturing candid emotions, stolen glances, and spontaneous joy.",
                },
                {
                  number: "02",
                  title: "Your Story, Your Way",
                  description: "No two weddings are alike. We take the time to understand your unique story, your style, and your vision — then we bring it to life through our lens.",
                },
                {
                  number: "03",
                  title: "Relaxed & Present",
                  description: "We work as unobtrusive observers, blending into your celebration so you can be fully present. The best photos happen when you forget we're there.",
                },
                {
                  number: "04",
                  title: "Timeless With a Modern Edge",
                  description: "Our editing style balances classic elegance with contemporary touches. These photos won't feel dated in 10, 20, or 50 years — they'll feel timeless.",
                },
              ].map((item, index) => (
                <motion.div
                  key={item.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-6 rounded-xl bg-card border border-border p-6"
                >
                  <span className="text-4xl font-bold text-primary/20">{item.number}</span>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-foreground">{item.title}</h3>
                    <p className="mt-2 text-muted-foreground">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding">
          <div className="container-narrow text-center">
            <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Ready to Tell Your Story?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Let's chat about your wedding day and how we can capture your unique love story.
            </p>
            <Button variant="hero" size="xl" className="mt-8" asChild>
              <Link to="/contact">Book a Consultation</Link>
            </Button>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default OurStory;
